// The front door: the west front of the Court (spec 2, row 3). Everything here is an object on the plaza (M10 Track
// D): the name is cast in a bronze panel on a marble stone in the foreground, the ways in are brass plates on a
// bronze stand at the foot of the steps (the main one lights the bronze doors behind it), and the calendar is posted
// in a notice case on the plaza. Each is placed over the object the art draws (world.json anchors front.title,
// front.stand, front.board, front.doors). The camera does the entrance: choosing a way in zooms out over the
// building and into the room it leads to.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { CaseSummary } from '../../lib/types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '../../lib/documentTitle';
import { CURRENT_TERM } from '../../lib/term';
import { DEFAULT_ARGUMENT_MINUTES, SESSION_MINUTES_CEILING } from '../../lib/sessionLength';
import { useWorldScene, WorldAnchor } from '../../world/WorldStage';
import { useWorldMeta } from '../../world/assets';
import { SFX } from '../../world/sfx';
import { Icon } from '../../ui/Icon';
import Footer from '../chrome/Footer';
import './landing.css';

export const TITLE = 'SCOTUS the Nine';
// One line on the cast panel in the pixel face, which is wider than the face it was first written for.
export const STRAPLINE = 'Argue aloud. Win five of nine.';

type Box = [number, number, number, number];
/** Where the art draws each object (map pixels: the west front sits at (720, 850)); used until world.json loads. */
const FALLBACK: Record<string, Box> = {
  'front.title': [882, 1080, 156, 25],
  'front.stand': [920, 1018, 80, 32],
  'front.board': [1130, 1008, 60, 70],
  'front.doors': [945, 966, 30, 53],
};

/** Art pixels per CSS pixel on the title screen (the camera fits the 480 x 270 front at a whole device scale). */
function frontWp(): number {
  if (typeof window === 'undefined') return 4;
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const s = Math.max(1, Math.floor(Math.min((window.innerWidth * dpr) / 480, (window.innerHeight * dpr) / 270) + 1e-6));
  return s / dpr;
}

/** How many cases fit on the notice case's face at this scale, each a 44 px row under the term's heading. */
export function boardRows(wp: number, faceH: number): number {
  return Math.max(3, Math.min(8, Math.floor((faceH * wp - 44) / 44)));
}

export default function Landing() {
  const navigate = useNavigate();
  const auth = useAuth();
  const m = useWorldMeta();
  const [activeGameId, setActiveGameId] = useState<string | null>(null);
  const [caseCount, setCaseCount] = useState<number | null>(null);
  const [calendar, setCalendar] = useState<CaseSummary[] | null>(null);
  const [featuredSlug, setFeaturedSlug] = useState<string | null>(null);
  const [wp, setWp] = useState(frontWp);
  const checkedRef = useRef(false);
  useDocumentTitle('');
  useWorldScene({ room: 'front', dim: false });

  useEffect(() => {
    const fit = () => setWp(frontWp());
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    if (checkedRef.current || auth.status !== 'ready') return;
    checkedRef.current = true;
    // An unfinished argument relabels the main call; it never redirects (stepping down must stay possible).
    api.getActiveGame().then((game) => { if (game) setActiveGameId(game.id); }).catch(() => {});
    api.getCases()
      .then((list) => { setCaseCount(list.length); setCalendar(list.slice(0, 8)); })
      .catch(() => { setCalendar([]); });
    // A finished argument a visitor can read without performing one.
    api.getFeaturedArgument().then((r) => setFeaturedSlug(r.slug)).catch(() => {});
  }, [auth.status]);

  const go = (to: string) => {
    SFX.click();
    // Only once the session is open: the plaza is drawn during the auth bootstrap, and a request made then would go
    // out without a token, come back 401 (which clears the stored token) and be shared with the docket's own fetch.
    if (to === '/home' && auth.status === 'ready') api.preloadCases();
    navigate(to);
    window.scrollTo(0, 0);
  };
  const at = (name: string): Box => {
    const a = m?.anchors?.[name];
    return a ? [a[0], a[1], a[2], a[3]] : FALLBACK[name];
  };
  const size = (b: Box) => ({ ['--aw' as string]: b[2], ['--ah' as string]: b[3] });

  // Drawn before the session is open (App renders the plaza during the auth
  // bootstrap); the effect above fetches its data once the session is ready.

  const title = at('front.title');
  const stand = at('front.stand');
  const board = at('front.board');
  const doors = at('front.doors');
  const rows = calendar ? calendar.slice(0, boardRows(wp, board[3])) : null;

  return (
    // <main>: this page renders outside the shell, so nothing else gives it a landmark.
    <main className="plaza full-screen-min-h" aria-labelledby="plaza-title">
      <WorldAnchor x={doors[0]} y={doors[1]} className="plaza-doors-anchor">
        <div className="plaza-doorlight" style={size(doors)} aria-hidden="true" />
      </WorldAnchor>
      <WorldAnchor x={title[0]} y={title[1]} className="plaza-title-anchor">
        <div className="plaza-panel" style={size(title)}>
          <h1 id="plaza-title" className="plaza-title">{TITLE}</h1>
          <p className="plaza-strap">{STRAPLINE}</p>
        </div>
      </WorldAnchor>
      <WorldAnchor x={stand[0] + stand[2] / 2} y={stand[1] + stand[3]} className="plaza-stand-anchor">
        <div className="plaza-stand">
          <button
            type="button"
            className="pb brass lg plaza-plate plaza-plate-main"
            onClick={() => go(activeGameId ? `/play/${activeGameId}` : '/home')}
            autoFocus
          >
            {activeGameId ? 'Resume your argument' : 'Step to the Lectern'}
          </button>
          {/* what a case costs, engraved on the stand under the way in (the figures come from sessionLength) */}
          <p className="plaza-cost">
            {DEFAULT_ARGUMENT_MINUTES} minutes at the lectern, under {SESSION_MINUTES_CEILING} for the whole case
          </p>
          <div className="plaza-second">
            <button type="button" className="pb brass plaza-plate" onClick={() => go('/home')}>
              <Icon name="book" />
              {caseCount === null ? 'The full docket' : `The full docket, all ${caseCount} cases`}
            </button>
            {featuredSlug !== null && (
              <button type="button" className="pb paper plaza-plate" data-testid="landing-read-argument" onClick={() => go(`/g/${featuredSlug}`)}>
                Or read an argument
              </button>
            )}
          </div>
        </div>
      </WorldAnchor>
      {rows !== null && rows.length > 0 && wp >= 3 && (
        <WorldAnchor x={board[0]} y={board[1]} className="plaza-board-anchor">
          <nav className="plaza-board" style={size(board)} aria-label="Cases on the calendar">
            <h2 className="plaza-board-title">{CURRENT_TERM.label}</h2>
            <ol>
              {rows.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="plaza-case doc"
                    aria-label={`Open ${c.name} on the docket`}
                    onClick={() => go(`/home?case=${encodeURIComponent(c.id)}`)}
                  >
                    <i>{c.name}</i>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        </WorldAnchor>
      )}
      <nav className="plaza-foot" aria-label="About this site">
        <button type="button" className="link-px ui" onClick={() => go('/about')}>About</button>
        <button type="button" className="link-px ui" onClick={() => go('/terms')}>Terms</button>
      </nav>
      <Footer />
    </main>
  );
}
